import { Container, Skeleton, Flex } from '@everlywell/ui-kit';
import BrandMomentBanner from 'components/BrandMomentBanner';
import { parseBrandMomentBanner } from 'components/BrandMomentBanner/BrandMomentBanner.parser';
import CtaBanner from 'components/CtaBanner';
import parseCtaBanner from 'components/CtaBanner/CtaBanner.parser';
import PressSectionV2 from 'components/Homepage/PressSectionV2';
import parsePressSectionV2Data from 'components/Homepage/PressSectionV2/PressSectionV2.parser';
import HomepageHero, { parseHomepageHero } from 'components/HomepageHero';
import Layout from 'components/Layout';
import ProductCarousel, {
  parseProductCarouselData,
} from 'components/ProductCarousel';
import ReassuranceCarousel from 'components/ReassuranceCarousel';
import { parseReassuranceCarouselData } from 'components/ReassuranceCarousel/ReassuranceCarousel.parser';
import { SEO } from 'components/SEO';
import parseSeoData from 'components/SEO/Seo.parser';
import ShapeCards from 'components/ShapeCards';
import { parseShapeCards } from 'components/ShapeCards/ShapeCards.parser';
import StoriesCarousel from 'components/StoriesCarousel';
import TabbedBanners from 'components/TabbedBanners';
import { parseTabbedBanners } from 'components/TabbedBanners/TabbedBanners.parser';
import TakeoverBanner from 'components/TakeoverBanner';
import { parseTakeoverBanner } from 'components/TakeoverBanner/TakeoverBanner.parser';
import TakeoverPromoSection from 'components/TakeoverPromoSection';
import { parseTakeoverPromoHomepage } from 'components/TakeoverPromoSection/TakeoverPromoSection.parser';
import TrackVisibilityWrapper from 'components/TrackVisibilityWrapper';
import { graphql, PageProps } from 'gatsby';
import React, { Suspense, useEffect } from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { logError } from 'utils/helpers';
import useAdvertisedPromos from 'utils/hooks/useAdvertisedPromos';
import { Metadata, Section, SeoData } from 'utils/types/contentfulTypes';

type Props = {
  pageContext: {
    pageMetadata?: Metadata;
    sections?: Section[];
    seo?: SeoData;
  };
};

/**
 * `ComponentMapping` is a type used to map component names to their respective paths and parsers.
 * Each key in the `ComponentMapping` object is a string representing the name of a component from Contentful.
 * The value associated with each key is an object containing the following properties:
 * - `parser`: A function used to parse the data for the component.
 * - `centerContent`: A boolean indicating whether the content should be centered. (margin: 0 auto)
 * - `flexContainer`: A boolean indicating whether the component should be a flex container.
 * - `width`: A string representing the width of the component container (TrackVisibilityWrapper).
 */
type ComponentMapping = Record<
  string,
  {
    component: React.FC<any>;
    parser:
      | ((section: Section) => any)
      | ((section: Queries.ContentfulSection001) => any)
      | ((section: Queries.ContentfulSection001, advertisedPromos: any) => any);
    centerContent?: boolean;
    flexContainer?: boolean;
    width?: string;
  }
>;

/**
 * Mappings for section components
 */
const components: ComponentMapping = {
  ReassuranceCarousel: {
    component: ReassuranceCarousel,
    parser: parseReassuranceCarouselData,
  },
  ProductCarousel: {
    component: ProductCarousel,
    parser: parseProductCarouselData,
  },
  TabbedBanners: {
    component: TabbedBanners,
    parser: parseTabbedBanners,
  },
  StoriesCarousel: {
    component: StoriesCarousel,
    parser: parseShapeCards,
  },
  ShapeCards: {
    component: ShapeCards,
    parser: parseShapeCards,
  },
  TakeoverBanner: {
    component: TakeoverBanner,
    parser: parseTakeoverBanner,
  },
  PressSectionV2: {
    component: PressSectionV2,
    parser: parsePressSectionV2Data,
  },
  CtaBanner: {
    component: CtaBanner,
    parser: parseCtaBanner,
    centerContent: true,
    flexContainer: true,
  },
  BrandMomentBanner: {
    component: BrandMomentBanner,
    parser: parseBrandMomentBanner,
  },
  HeroCarousel: {
    component: HomepageHero,
    parser: parseHomepageHero,
    centerContent: true,
    width: '100%',
  },
  TakeoverPromo: {
    component: TakeoverPromoSection,
    parser: parseTakeoverPromoHomepage,
  },
};

export type HomepageProps = {} & PageProps<Queries.HomepageQuery> & Props;

export default function Homepage(props: HomepageProps) {
  const pageData = props?.data?.allContentfulPage001?.nodes[0];

  const { advertisedPromos } = useAdvertisedPromos();

  useEffect(() => {
    analytics.track({
      event: ANALYTICS.EVENTS.VIEWED_PAGE,
      data: {
        Page: 'Homepage V2',
      },
    });
  }, []);

  // If there is a problem with the query, log the error.
  if (!pageData) {
    logError('Error fetching homepage V2 data', {
      component: 'Homepage V2',
      method: 'Contenful Query',
    });

    return null;
  }

  return (
    <>
      {pageData?.seo ? (
        <SEO {...parseSeoData(pageData.seo as SeoData)} />
      ) : null}
      <Layout isV2Page>
        <Flex
          width="100%"
          overflowX="hidden"
          flexDirection="column"
          gap={[10, null, 20]}
          sx={{
            '& > :last-child': {
              // Add margin to the last child to prevent it from sticking to the bottom of the page. Gap doesn't add space to the last child.
              marginBottom: ['10 !important', null, '20 !important'],
            },
          }}
        >
          {/* Add sections here */}
          {pageData.sections?.map((section, sectionIndex) => {
            // Additional null check for section
            if (!section || !section.componentName) {
              return null;
            }

            // Get the component from the mapping
            const sectionComponent =
              components[section.componentName as keyof typeof components];

            if (
              !sectionComponent ||
              !sectionComponent.parser ||
              !sectionComponent.component
            ) {
              logError('Component not found', {
                component: 'Homepage V2',
                method: 'getComponent',
                componentName: section.componentName,
              });
              return null;
            }
            // We are in a transition period where some sections are still using types defined by us instead of auto generated types
            let componentProps;

            // We are in a transition period where some sections are still using types defined by us instead of auto generated types
            if (section.componentName === 'ProductCarousel') {
              componentProps = sectionComponent.parser(
                // @ts-ignore
                section,
                advertisedPromos,
              );
            } else {
              // @ts-ignore
              componentProps = sectionComponent.parser(section);
            }

            return (
              <Suspense
                fallback={
                  <Container
                    size={['sm', null, 'xl']}
                    marginX="auto"
                    marginY="6"
                  >
                    <Skeleton height="500px"></Skeleton>
                  </Container>
                }
                key={`${section.componentName}-${sectionIndex}`}
              >
                <TrackVisibilityWrapper
                  label={section.componentName || 'No component name provided'}
                  slug={section.displayName || ''}
                  centerContent={!!sectionComponent?.centerContent}
                  flexContainer={!!sectionComponent?.flexContainer}
                  width={sectionComponent?.width}
                  data-vwo-component={`${section.componentName}-${sectionIndex}`}
                  // todo: remove this once the test is done
                  style={{
                    display:
                      `${section.componentName}-${sectionIndex}` ===
                      'TakeoverPromo-1'
                        ? 'none'
                        : undefined,
                  }}
                >
                  {React.createElement(
                    sectionComponent.component,
                    componentProps,
                  )}
                </TrackVisibilityWrapper>
              </Suspense>
            );
          })}
        </Flex>
      </Layout>
    </>
  );
}

export const data = graphql`
  fragment HomepageSubsection on ContentfulSection001 {
    subsections {
      ... on ContentfulSection001 {
        componentName
        displayName
        eyebrow
        body {
          raw
        }
        images {
          desktopImageAltText
          desktopImage {
            gatsbyImageData(quality: 75, placeholder: BLURRED)
          }
          mobileImageAltText
          mobileImage {
            gatsbyImageData(quality: 75, placeholder: BLURRED)
          }
        }
        metadata {
          title
          description {
            description
          }
          context {
            products {
              slug
            }
            icon
            highlight_carousel_item {
              icon
              color
            }
          }
          ctas {
            label
            url
          }
          theme {
            backgroundColor
            textColor
          }
        }
      }
      ... on ContentfulCarousel001 {
        items {
          ... on ContentfulCarouselItem001 {
            content {
              subsections {
                ... on ContentfulProduct001 {
                  slug
                }
              }
            }
          }
        }
      }
    }
  }

  fragment HomepageSubsections on ContentfulSection001 {
    ...HomepageSubsection
    subsections {
      ...HomepageSubsection
      ... on ContentfulSection001 {
        ...HomepageSubsection
      }
    }
  }

  fragment HomepageSection on ContentfulSection001 {
    componentName
    eyebrow
    displayName
    body {
      raw
    }
    images {
      desktopImageAltText
      desktopImage {
        gatsbyImageData(quality: 75, placeholder: BLURRED)
        file {
          url
        }
      }
      mobileImageAltText
      mobileImage {
        gatsbyImageData(quality: 75, placeholder: BLURRED)
        file {
          url
        }
      }
    }
    metadata {
      title
      description {
        description
        childMarkdownRemark {
          html
        }
      }
      ctas {
        displayName
        label
        url
        metadata {
          context {
            ctaParts {
              priceText
              startingText
              billFrequencyText
              icon
            }
          }
        }
      }
      theme {
        backgroundColor
        textColor
        primaryColor
        secondaryColor
      }
      flags {
        displayName
        displayText
        value
      }
      context {
        titleTag
      }
    }
    ...HomepageSubsections
  }

  query Homepage {
    allContentfulPage001(filter: { slug: { eq: "homepage" } }, limit: 1) {
      nodes {
        metadata {
          flags {
            displayText
            value
          }
        }
        slug
        sections {
          ...HomepageSection
        }
        seo {
          canonicalUrl
          metaTitle
          metaDescription {
            metaDescription
          }
          metaKeywords
          faqSchema {
            question
            answer
          }
        }
      }
    }
  }
`;
